import { useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import UndoAnimatedIcon from '@/components/ui/UndoAnimatedIcon';
import type { ApiKey } from './types';

// ── Constants ────────────────────────────────────────────────────────────────

const API_URL =
  import.meta.env.VITE_API_BASE_URL ??
  'https://api.appjoshua.com';

type Tab = 'rest' | 'mcp' | 'nocode';

const TAB_LABELS: Record<Tab, string> = {
  rest: 'REST API',
  mcp: 'MCP',
  nocode: 'No-code',
};

// ── Styles ───────────────────────────────────────────────────────────────────

const BACKDROP_STYLE: CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 10001,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const MODAL_STYLE: CSSProperties = {
  background: 'white',
  borderRadius: '12px',
  maxWidth: '600px',
  width: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const CODE_STYLE: CSSProperties = {
  display: 'block',
  padding: '14px 16px',
  background: '#0f172a',
  color: '#e2e8f0',
  borderRadius: '6px',
  fontSize: '12px',
  fontFamily: 'monospace',
  lineHeight: '1.6',
  whiteSpace: 'pre',
  overflowX: 'auto',
  margin: '8px 0 0',
};

// ── Copy button helper ───────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API not available — silently ignore
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copiado!' : 'Copiar'}
      style={{
        border: 'none',
        background: copied ? '#dcfce7' : '#f3f4f6',
        color: copied ? '#16a34a' : '#374151',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '12px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 500,
        transition: 'background 0.15s',
      }}
      aria-label={copied ? 'Copiado' : 'Copiar para área de transferência'}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copiado!' : 'Copiar'}
    </button>
  );
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface PostCreationModalProps {
  raw: string;
  record: ApiKey;
  onClose: () => void;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function PostCreationModal({
  raw,
  record,
  onClose,
}: PostCreationModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('rest');

  const curlExample = `curl -s \\
  -H "Authorization: Bearer ${raw}" \\
  "${API_URL}/v1/me"`;

  const mcpConfig = JSON.stringify(
    {
      mcpServers: {
        joshua: {
          command: 'npx',
          args: ['@joshua/mcp-server'],
          env: {
            JOSHUA_API_KEY: raw,
            JOSHUA_API_URL: API_URL,
          },
        },
      },
    },
    null,
    2
  );

  return createPortal(
    <div
      style={BACKDROP_STYLE}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={MODAL_STYLE} role="dialog" aria-modal="true" aria-label="Chave criada com sucesso">

        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--panel-border, #e5e7eb)',
          background: '#f0f9ff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Check size={18} color="#16a34a" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#154680' }}>
                Chave criada: {record.name}
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                Prefixo: <code style={{ fontFamily: 'monospace', color: '#154680' }}>{record.prefix}…</code>
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>

          {/* Warning banner */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px 14px',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            marginBottom: '20px',
          }}>
            <AlertTriangle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: '1px' }} />
            <div style={{ fontSize: '13px', lineHeight: '1.5', color: '#78350f' }}>
              <strong>Esta é a única vez que a chave completa será exibida.</strong>
              {' '}Copie e guarde em local seguro agora. Após fechar este modal, não será possível recuperá-la.
            </div>
          </div>

          {/* Raw key + copy */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px',
            }}>
              <label style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
              }}>
                Chave de API
              </label>
              <CopyButton text={raw} />
            </div>
            <div style={{
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1.5px solid #154680',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '13px',
              color: '#154680',
              wordBreak: 'break-all',
              lineHeight: '1.5',
            }}>
              {raw}
            </div>
          </div>

          {/* Usage tabs */}
          <div>
            <p style={{
              margin: '0 0 10px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
            }}>
              Como usar
            </p>

            {/* Tab bar */}
            <div style={{
              display: 'flex',
              gap: '2px',
              borderBottom: '2px solid #e5e7eb',
              marginBottom: '16px',
            }}>
              {(['rest', 'mcp', 'nocode'] as Tab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    padding: '8px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: activeTab === tab ? '#154680' : '#6b7280',
                    borderBottom: activeTab === tab
                      ? '2px solid #154680'
                      : '2px solid transparent',
                    marginBottom: '-2px',
                    transition: 'color 0.15s',
                  }}
                >
                  {TAB_LABELS[tab]}
                </button>
              ))}
            </div>

            {/* REST tab */}
            {activeTab === 'rest' && (
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#374151' }}>
                  Inclua o header <code style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '1px 5px', borderRadius: '3px' }}>Authorization: Bearer &lt;chave&gt;</code> em todas as requisições:
                </p>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '18px', right: '10px', zIndex: 1 }}>
                    <CopyButton text={curlExample} />
                  </div>
                  <code style={CODE_STYLE}>{curlExample}</code>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  Documentação completa da API em <code style={{ fontFamily: 'monospace' }}>{API_URL}/v1/docs</code>
                </p>
              </div>
            )}

            {/* MCP tab */}
            {activeTab === 'mcp' && (
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#374151' }}>
                  Adicione ao seu <code style={{ fontFamily: 'monospace', background: '#f3f4f6', padding: '1px 5px', borderRadius: '3px' }}>claude_desktop_config.json</code>:
                </p>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: '18px', right: '10px', zIndex: 1 }}>
                    <CopyButton text={mcpConfig} />
                  </div>
                  <code style={CODE_STYLE}>{mcpConfig}</code>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#6b7280' }}>
                  O pacote <code style={{ fontFamily: 'monospace' }}>@joshua/mcp-server</code> será publicado em breve (Phase 4).
                </p>
              </div>
            )}

            {/* No-code tab */}
            {activeTab === 'nocode' && (
              <div style={{
                padding: '24px',
                textAlign: 'center',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px dashed #d1d5db',
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
                  Em breve: templates Zapier / Make / n8n
                </p>
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                  Templates prontos para conectar Joshua ao seu stack de automação sem código.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--panel-border, #e5e7eb)',
          display: 'flex',
          justifyContent: 'flex-end',
          background: '#f9fafb',
        }}>
          <button
            className="btn-add undo-animated"
            onClick={onClose}
          >
            <UndoAnimatedIcon size={15} />
            <span className="btn-text">Fechar</span>
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
